import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

export interface IcsPicture {
  image: string | undefined,
  filename: string,
  hasAnnotation?: boolean,
  annotationRevision?: number,
  annotationUpdatedAt?: string
}
export interface IcsPictureEx {
  pic: IcsPicture,
  mark: boolean
}

export interface IcsPictureComponentConfigure {
  gridSize?: number,
  refresh?: boolean,
  delete?: boolean,
  download?: boolean,
  cutAndPaste?: boolean,
  selectable?: boolean,
  infiniteScroll?: boolean
}

@Component({
  standalone: false,
  selector: 'ics-picture-view',
  templateUrl: './pic-folder.component.html',
  styleUrls: ['./pic-folder.component.scss'],
})
export class PicFolderComponent implements OnChanges {
  @Input() pics: IcsPicture[] = [];
  @Input() pasteButton: boolean = false;
  @Input() downloadInProgress = false;
  private readonly defaultConfigure: IcsPictureComponentConfigure = {
    gridSize: 3,
    refresh: true,
    delete: true,
    download: false,
    cutAndPaste: true,
    selectable: true,
    infiniteScroll: false,
  };
  private pictureViewConfig: IcsPictureComponentConfigure = { ...this.defaultConfigure };

  /**
   * 呼び出し側はグリッド列数だけなど、一部の設定だけを渡せる。
   * 画面回転時にもInputは再評価されるため、毎回デフォルト設定と統合する。
   */
  @Input()
  set config(config: IcsPictureComponentConfigure | null | undefined) {
    this.pictureViewConfig = { ...this.defaultConfigure, ...(config ?? {}) };
  }
  get config(): IcsPictureComponentConfigure {
    return this.pictureViewConfig;
  }
  
  @Output() del = new EventEmitter<string[]>();
  @Output() downloadSelected = new EventEmitter<string[]>();
  @Output() cut = new EventEmitter<string[]>();
  @Output() paste = new EventEmitter<void>();
  @Output() infiniteDataRequest = new EventEmitter<void>();
  @Output() refreshView = new EventEmitter<void>();

  async doInfiniteDataRequest(event: any) {
    event.target.complete();
  }

  @Output() selected = new EventEmitter<string>();

  private selectMode = false;
  public pictures: IcsPictureEx[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pics']) {
      this.pictures = [];
      this.pics.forEach(pic => {
        this.pictures.push({ pic: pic, mark: false });
      });
    }
  }
  
  getGridStyle() {
    return {
      'grid-template-columns': `repeat(${this.config.gridSize}, 1fr)`
    };
  }

  loadMoreData(event: any) {
    this.infiniteDataRequest.emit(event);
  }

  refreshData(event: any) {
    this.refreshView.emit(event);
  }

  setSelectMode(mode: boolean) {
    this.selectMode = mode;
  }

  getSelectMode() {
    return this.selectMode && this.config.selectable;
  }

  clickPicture(picture: IcsPictureEx) {
    if (this.config.selectable && this.selectMode) {
      picture.mark = !picture.mark;
    } else {
      this.selected.emit(picture.pic.filename);
    }
  }

  deletePictures() {
    if (this.config.delete) {
      const deletePictures = this.pictures.filter(pic => pic.mark).map(pic => pic.pic.filename);
      //あくまでも表示とコントロールのみに専念するためデータ操作はしない
      //this.pictures = this.pictures.filter(pic => !pic.mark);
      //this.pics = this.pics.filter(pic => !this.pictures.some(p => p.pic.filename === pic.filename && p.mark));
      this.del.emit(deletePictures);
    }
  }

  downloadPictures() {
    if (this.config.download && !this.downloadInProgress) {
      this.downloadSelected.emit(this.selectedPictureFilenames());
    }
  }

  cutPictures() {
    if (this.config.cutAndPaste) {
      const cutPictures = this.pictures.filter(pic => pic.mark).map(pic => pic.pic.filename);
      this.cut.emit(cutPictures);
    }
  }
  
  pastePictures() {
    if (this.config.cutAndPaste) {
      this.paste.emit();
    }
  }

  cancelSelectMode() {
    this.setSelectMode(false);
    this.pictures.forEach(pic => pic.mark = false);
  }

  selectAllPictures() {
    this.setSelectMode(true);
    this.pictures.forEach(pic => pic.mark = true);
  }

  deSelectAllPictures() {
    this.pictures.forEach(pic => pic.mark = false);
  }

  hasSelectedPictures(): boolean {
    return this.pictures.some(picture => picture.mark);
  }

  private selectedPictureFilenames(): string[] {
    return this.pictures.filter(picture => picture.mark).map(picture => picture.pic.filename);
  }
}
